import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./auth";
import { subscribe, unsubscribe } from "../utils/events";
import NotificationsService from "../services/NotificationsService";
import { getNotificationTitle } from "../components/Notifications/NotificationCard";
import { toast } from "react-toastify";
import { DefaultNotification } from "../toasts/DefaultNotification";
import { NotificationCategoryNames } from "../constants/notifications.constants";
import { removeSpecialChars } from "../utils/string";

const NotificationsContext = createContext({
	notifications: [],
	deleteTarget: null,
	readTarget: null,
	readAll: null,
	isLoading: false,
	loadNotifications: null,
});

export function isCategoryAlert(notification) {
	const categoryName = NotificationCategoryNames[Number(notification.categoryId)];
	return categoryName.toLowerCase().includes('alarm') || removeSpecialChars(categoryName.toLowerCase()).includes('panico');
}

export const NotificationsProvider = ({ children }) => {
	const [notifications, setNotifications] = useState([]);
	const [total, setTotal] = useState(0);
	const [totalUnread, setTotalUnread] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [categorySearchBy, setCategorySearchBy] = useState('');
	const { user } = useAuth();

	async function loadNotifications(reset) {
		if (!user?.userData) return;
		setIsLoading(true);
		const response = await NotificationsService.listTargets({ categorySearchBy, offset: reset ? 0 : notifications.length, itemsPerPage: 12 });
		const totalItems = response?.data?.pagination?.totalItems ?? 0;
		setIsLoading(false);
		setTotal(reset ? totalItems : totalItems + total);
		setTotalUnread(response.data?.totalUnread ?? 0);
		const loadedNotifications = response?.data?.items ?? [];
		updateNotifications(notifications => reset ? loadedNotifications : [...notifications, ...loadedNotifications]);
	}

	useEffect(() => {
		console.log('user changed: ', user);
		loadNotifications(true);
		subscribe('onSseReceived', onSseReceived);
		return () => {
			unsubscribe('onSseReceived', onSseReceived);
		}
	}, [user])

	useEffect(() => {
		loadNotifications(true);
	}, [categorySearchBy])

	function updateNotifications(set) {
		setNotifications(notifications => {
			return set(notifications)?.sort((a, b) => {
				return new Date(b.deliveredAt).getTime() - new Date(a.deliveredAt).getTime();
			});
		});
	}

	async function deleteTarget(id, onlyState = false) {
		if (!onlyState) {
			await NotificationsService.deleteTarget(id);
		}
		updateNotifications(notifications => notifications.filter(notification => notification.id !== id));
	}

	async function readTarget(id) {
		setIsLoading(true)
		const { data } = await NotificationsService.readTarget(id);
		setIsLoading(false)
		if (data.readAt) {
			const notificationIdx = notifications.findIndex(notification => notification.externalId === id);
			if (notificationIdx !== -1) {
				const updatedNotifications = [...notifications];
				updatedNotifications[notificationIdx].readAt = data.readAt;
				setTotalUnread(total => Math.max(total - 1, 0))
				updateNotifications(notifications => updatedNotifications);
			}
		}
	}

	async function readAll(readAt) {
		setIsLoading(true)
		const { data } = await NotificationsService.readAllTargets();
		setIsLoading(false)
		if (data.readAt) {
			updateNotifications(notifications => notifications.map(notification => ({
				...notification,
				readAt,
			})));
			setTotalUnread(0);
		}
	}

	function onSseReceived(ssePayload) {
		if (ssePayload.action === 'newNotification') {
			const hasTarget = notifications.find(notification => notification.id === ssePayload.content.id);
			if (!hasTarget) {
				const isAlert = isCategoryAlert(ssePayload.content.notification);
				toast(DefaultNotification, {
					data: {
						title: getNotificationTitle(ssePayload.content),
						message: ssePayload.content.message,
						isAlert,
					},
					progressClassName: isAlert && 'toast-progress-alarm',
					className: isAlert && 'toast-alarm',
					icon: false,
				})
				if (!ssePayload.content.readAt) {
					setTotalUnread(total => total + 1);
				}
				updateNotifications(notifications => [...notifications, ssePayload.content]);
			}
		}
		if (ssePayload.action === 'deletedNotification') {
			if (!ssePayload.content.readAt) {
				setTotalUnread(total => Math.max(total - 1, 0))
			}
			deleteTarget(ssePayload.content.id, true);
		}
	}

	return (
		<NotificationsContext.Provider value={{ notifications, deleteTarget, readTarget, readAll, isLoading, loadNotifications, totalUnread, setCategorySearchBy }}>
			{children}
		</NotificationsContext.Provider>
	);
}

export function useNotifications() {
	const context = useContext(NotificationsContext);
	return context;
}